import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Minus, Plus, Trash2, ArrowLeft, ShoppingBag } from 'lucide-react';
import { auth, db } from '../../utils/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { useQuote } from '../Quote/QuoteContext';
import { useUserScheme } from '../UserSchemeContext';
import { toast } from 'sonner';

export default function Cart({ cart, setCart }) {
    const { openQuote } = useQuote();
    const contextSchemeId = useUserScheme();
    const [schemeId, setSchemeId] = useState(null);
    const [showEasyOrderInput, setShowEasyOrderInput] = useState(false);
    const [easyOrderListName, setEasyOrderListName] = useState('');
    const [existingEasyOrders, setExistingEasyOrders] = useState([]);
    const [selectedEasyOrderId, setSelectedEasyOrderId] = useState('new');
    const [isSavingEasyOrder, setIsSavingEasyOrder] = useState(false);

    const fetchExistingEasyOrders = async () => {
        if (!auth.currentUser) return;
        try {
            const { collection: _collection, getDocs: _getDocs } = await import('firebase/firestore');
            const easyOrdersRef = _collection(db, "users", auth.currentUser.uid, "easy_orders");
            const snap = await _getDocs(easyOrdersRef);
            const list = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setExistingEasyOrders(list);
        } catch (e) {
            console.error("Error loading existing easy orders:", e);
        }
    };

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(async (user) => {
            if (user) {
                try {
                    let userDocRef = doc(db, "users", user.uid);
                    let docSnap = await getDoc(userDocRef);
                    if (!docSnap.exists()) {
                        userDocRef = doc(db, "users", user.email.toLowerCase());
                        docSnap = await getDoc(userDocRef);
                    }
                    if (docSnap.exists()) {
                        const data = docSnap.data();
                        const sId = data.scheme_id || data.schema_id || data.schemeId || null;
                        setSchemeId(sId);
                    } else {
                        setSchemeId(null);
                    }
                } catch (e) {
                    console.error("Error loading user scheme_id in Cart page", e);
                    setSchemeId(null);
                }
            } else {
                setSchemeId(null);
            }
        });
        return () => unsubscribe();
    }, []);

    // Ensure every cart item quantity is at least minimum_order on load/change
    useEffect(() => {
        setCart(prev => prev.map(item => {
            const minQty = item.product?.minimum_order || 1;
            if (!item.quantity || Number(item.quantity) < minQty) {
                return { ...item, quantity: minQty };
            }
            return item;
        }));
    }, []);


    const activeSchemeId = schemeId || contextSchemeId;

    const findSchemePricing = (product, sId = activeSchemeId) => {
        if (!product || !sId) return null;

        if (product.pricing && Array.isArray(product.pricing)) {
            const direct = product.pricing.find(p =>
                p && String(p.scheme_id || p.schema_id || p.schemeId || '') === String(sId)
            );
            if (direct) return direct;
        }

        if (product.schemeIds && Array.isArray(product.schemeIds) && product.pricing && Array.isArray(product.pricing)) {
            const idx = product.schemeIds.findIndex(id => String(id) === String(sId));
            if (idx !== -1 && product.pricing[idx]) {
                return product.pricing[idx];
            }
        }

        return null;
    };

    const getProductPriceForQty = (product, qty) => {
        const schemePricing = findSchemePricing(product);

        if (schemePricing && schemePricing.tiers && schemePricing.tiers.length > 0) {
            const ascTiers = [...schemePricing.tiers].sort((a, b) => {
                const qa = Number(a.min ?? a.min_qty ?? a.minQty ?? 0);
                const qb = Number(b.min ?? b.min_qty ?? b.minQty ?? 0);
                return qa - qb;
            });

            const matched = [...ascTiers].reverse().find(t => qty >= Number(t.min ?? t.min_qty ?? t.minQty ?? 0));
            if (matched && matched.price !== undefined && matched.price !== null && Number(matched.price) > 0) {
                return Number(matched.price);
            }
            if (ascTiers[0] && ascTiers[0].price !== undefined && ascTiers[0].price !== null && Number(ascTiers[0].price) > 0) {
                return Number(ascTiers[0].price);
            }
        }

        if (product.price !== undefined && product.price !== null && Number(product.price) > 0) {
            return Number(product.price);
        }

        return 0;
    };

    const getActiveProductTiers = (product) => {
        const schemePricing = findSchemePricing(product);
        if (!schemePricing || !schemePricing.tiers || schemePricing.tiers.length === 0) return [];

        return [...schemePricing.tiers].sort((a, b) => {
            const qa = Number(a.min ?? a.min_qty ?? a.minQty ?? 0);
            const qb = Number(b.min ?? b.min_qty ?? b.minQty ?? 0);
            return qa - qb;
        });
    };

    // Returns true only when valid pricing exists for the product and scheme
    const hasPricingForProduct = (product) => {
        const price = getProductPriceForQty(product, 1);
        return typeof price === 'number' && price > 0;
    };

    const getProductCode = (p) => {
        if (!p) return '';
        if (p.code) return p.code;
        if (p.productCodeLetters && p.productCode) return `${p.productCodeLetters}${p.productCode}`;
        return p.productCode || '';
    };

    const getProductTitle = (p) => {
        if (!p) return '';
        return p.title || p.productName || '';
    };

    const getProductId = (p) => {
        if (!p) return '';
        return p.id || p.objectID || '';
    };

    const areProductsEqual = (p1, p2) => {
        const id1 = getProductId(p1);
        const id2 = getProductId(p2);
        if (id1 && id2) return id1 === id2;

        const code1 = getProductCode(p1);
        const code2 = getProductCode(p2);
        if (code1 && code2) return code1 === code2;

        const title1 = getProductTitle(p1);
        const title2 = getProductTitle(p2);
        return !!(title1 && title2 && title1 === title2);
    };

    const updateQuantity = (product, newQty) => {
        const minimum_order = product.minimum_order || 1;
        if (newQty < minimum_order) {
            toast.error(`Minimum order quantity is ${minimum_order}`);
            return;
        }
        if (product.stock !== undefined && newQty > product.stock) {
            toast.error("Out of stock");
        }
        setCart(prev => prev.map(item => {
            const match = areProductsEqual(item.product, product);
            return match ? { ...item, quantity: newQty } : item;
        }));
    };

    const handleQtyChange = (prod, e) => {
        const val = e.target.value;
        const minimum_order = prod.minimum_order || 1;
        if (val === '') {
            setCart(prev => prev.map(item => {
                const match = areProductsEqual(item.product, prod);
                return match ? { ...item, quantity: '' } : item;
            }));
        } else {
            const parsed = parseInt(val, 10);
            if (!isNaN(parsed) && parsed >= minimum_order) {
                if (prod.stock !== undefined && parsed > prod.stock) {
                    toast.error("Out of stock");
                }
                setCart(prev => prev.map(item => {
                    const match = areProductsEqual(item.product, prod);
                    return match ? { ...item, quantity: parsed } : item;
                }));
            }
        }
    };

    const handleQtyBlur = (prod, currentItem) => {
        const minimum_order = prod.minimum_order || 1;
        if (currentItem.quantity === '' || currentItem.quantity < minimum_order) {
            toast.error(`Minimum order quantity is ${minimum_order}`);
            setCart(prev => prev.map(item => {
                const match = areProductsEqual(item.product, prod);
                return match ? { ...item, quantity: minimum_order } : item;
            }));
        }
    };

    const removeItem = (prod) => {
        setCart(prev => prev.filter(item => {
            const match = areProductsEqual(item.product, prod);
            return !match;
        }));
        toast.success("Item removed from cart");
    };

    const handleSaveEasyOrder = async () => {
        if (!auth.currentUser) {
            toast.error("Please sign in to save templates.");
            return;
        }

        setIsSavingEasyOrder(true);
        try {
            const uid = auth.currentUser.uid;
            const { collection: _collection, addDoc: _addDoc, doc: _doc, getDoc: _getDoc, updateDoc: _updateDoc } = await import('firebase/firestore');

            const itemsToSave = cart.map(item => ({
                product_id: item.product.id || item.product.objectID || '',
                quantity: item.quantity,
                title: item.product.title || '',
                image: item.product.image || '/logo.png',
                code: item.product.code || '',
                price: item.product.price || item.product.distributorPrice || 0
            }));

            if (selectedEasyOrderId === 'new') {
                if (!easyOrderListName.trim()) {
                    toast.error("Please enter a name for your easy order template.");
                    setIsSavingEasyOrder(false);
                    return;
                }

                const easyOrdersRef = _collection(db, "users", uid, "easy_orders");
                await _addDoc(easyOrdersRef, {
                    name: easyOrderListName.trim(),
                    items: itemsToSave,
                    createdAt: new Date().toISOString()
                });

                toast.success("Easy Order template created successfully!");
            } else {
                const targetDocRef = _doc(db, "users", uid, "easy_orders", selectedEasyOrderId);
                const targetSnap = await _getDoc(targetDocRef);

                if (!targetSnap.exists()) {
                    toast.error("Selected list no longer exists.");
                    setIsSavingEasyOrder(false);
                    return;
                }

                const targetData = targetSnap.data();
                let currentItems = [...(targetData.items || [])];

                itemsToSave.forEach(newItem => {
                    const existingIdx = currentItems.findIndex(ci =>
                        (newItem.product_id && ci.product_id === newItem.product_id) ||
                        (newItem.code && ci.code === newItem.code) ||
                        (newItem.title && ci.title === newItem.title)
                    );
                    if (existingIdx > -1) {
                        currentItems[existingIdx] = {
                            ...currentItems[existingIdx],
                            quantity: Number(currentItems[existingIdx].quantity || 0) + Number(newItem.quantity || 0)
                        };
                    } else {
                        currentItems.push(newItem);
                    }
                });

                await _updateDoc(targetDocRef, {
                    items: currentItems,
                    updatedAt: new Date().toISOString()
                });

                toast.success(`Updated "${targetData.name}" template with cart items!`);
            }

            setShowEasyOrderInput(false);
            setEasyOrderListName('');
            setSelectedEasyOrderId('new');
        } catch (e) {
            console.error("Error saving easy order template:", e);
            toast.error("Failed to save template. Please try again.");
        } finally {
            setIsSavingEasyOrder(false);
        }
    };

    // Subtotal only over items that have scheme pricing
    const subtotal = cart.reduce((sum, item) => {
        if (!hasPricingForProduct(item.product)) return sum;
        const price = getProductPriceForQty(item.product, Number(item.quantity) || 0);
        return sum + (price * (Number(item.quantity) || 0));
    }, 0);

    // Whether ANY item in the cart lacks pricing (drives quote button visibility)
    const hasUnpricedItems = cart.some(item => !hasPricingForProduct(item.product));

    // Whether ANY item in the cart has insufficient stock
    const hasInsufficientStockItems = cart.some(item =>
        item.product.stock !== undefined &&
        (item.quantity > item.product.stock || item.product.stock < (item.product.minimum_order || 1))
    );

    return (
        <div className="bg-[#f8fafc] min-h-screen py-12">
            <div className="max-w-[1700px] mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="flex items-center gap-3 mb-10 text-left">
                    <div className="w-12 h-12 bg-white text-[#2C3E2B] rounded-2xl flex items-center justify-center shadow-sm border border-slate-100">
                        <ShoppingBag className="w-6 h-6" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-black text-[#2B3C4E] leading-tight m-0">Shopping Cart</h1>
                        <p className="text-sm text-slate-500 font-semibold mt-1 mb-0">
                            {cart.length} {cart.length === 1 ? 'item' : 'items'} in your cart
                        </p>
                    </div>
                </div>

                {cart.length > 0 ? (
                      
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
                        {/* Cart Items List */}
                        <div className="lg:col-span-2 bg-white rounded-3xl border border-slate-100 shadow-xs overflow-hidden">
                            {cart.map((item, idx) => {
                                const prod = item.product;
                                const itemPrice = getProductPriceForQty(prod, Number(item.quantity) || 0);
                                const tiersList = getActiveProductTiers(prod);
                                console.log("prod",prod);
                                return (
                                    
                                    <div key={idx} className={`p-6 flex flex-col sm:flex-row gap-6 items-start justify-between transition-all hover:bg-slate-50 text-left ${idx !== cart.length - 1 ? 'border-b border-slate-100' : ''}`}>
                                        <div className="flex gap-4 flex-1 min-w-0">
                                            {/* Image */}
                                            <div className="w-20 h-20 bg-slate-50 border border-slate-200/60 rounded-xl flex items-center justify-center p-2 flex-shrink-0 overflow-hidden">
                                                <img
                                                    src={prod.image || '/logo.png'}
                                                    alt={prod.title}
                                                    className="w-full h-full object-contain"
                                                    onError={(e) => { e.target.onerror = null; e.target.src = '/logo.png'; }}
                                                />
                                            </div>

                                            {/* Details */}
                                            <div className="flex-1 min-w-0">
                                                <Link to={`/product/${prod.id}`} className="no-underline">
                                                    <h3 className="text-base font-extrabold text-[#2C3E2B] hover:text-[#9E2A2B] leading-tight line-clamp-2 m-0 transition-colors">
                                                        {prod.title}
                                                    </h3>
                                                </Link>
                                                <p className="text-xs text-slate-500 font-semibold mt-1.5 mb-2 flex items-center gap-1.5">
                                                    <span>SKU: {prod.code || 'N/A'}</span>
                                                    {prod.brand && (
                                                        <>
                                                            <span className="w-1 h-1 rounded-full bg-slate-300"></span>
                                                            <span>{prod.brand}</span>
                                                        </>
                                                    )}
                                                </p>
                                                {prod.taxApplicable && (
                                                    <span className="inline-flex items-center text-[9px] font-black uppercase tracking-wide bg-amber-50 text-amber-600 border border-amber-200 px-1.5 py-0.5 rounded-md mb-2">
                                                        +Tax Applicable
                                                    </span>
                                                )}
                                                {prod.stock !== undefined && prod.stock < (prod.minimum_order || 1) ? (
                                                    <span className="inline-flex items-center text-[9px] font-black uppercase tracking-wide bg-red-50 text-red-600 border border-red-200 px-1.5 py-0.5 rounded-md mb-2 ml-2">
                                                        Out of Stock
                                                    </span>
                                                ) : prod.stock !== undefined && item.quantity > prod.stock ? (
                                                    <span className="inline-flex items-center text-[9px] font-black uppercase tracking-wide bg-amber-50 text-amber-600 border border-amber-200 px-1.5 py-0.5 rounded-md mb-2 ml-2">
                                                        Insufficient Stock (Available: {prod.stock})
                                                    </span>
                                                ) : null}

                                                {/* Dynamic Price Tier Options */}
                                                {tiersList.length > 0 && (
                                                    <div className="mt-3">
                                                        <span className="text-[10px] font-black text-slate-500 uppercase tracking-wider block mb-1.5">Volume Tiers</span>
                                                        <div className="flex flex-wrap gap-1.5">
                                                            {tiersList.map((tier, tIdx) => {
                                                                const minVal = Number(tier.min || tier.min_qty || tier.minQty || 0);
                                                                const isActive = Number(item.quantity) >= minVal &&
                                                                    (tIdx === tiersList.length - 1 || Number(item.quantity) < Number(tiersList[tIdx + 1].min || tiersList[tIdx + 1].min_qty || tiersList[tIdx + 1].minQty || 0));
                                                                return (
                                                                    <button
                                                                        key={tIdx}
                                                                        onClick={() => updateQuantity(prod, minVal)}
                                                                        className={`px-2.5 py-1 rounded-lg text-[10px] font-black cursor-pointer transition border ${isActive
                                                                            ? 'bg-[#2C3E2B] text-white border-[#2C3E2B] shadow-xs'
                                                                            : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                                                                            }`}
                                                                    >
                                                                        {minVal}+ Qty: ₹{Number(tier.price || 0).toFixed(2)}
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Actions & Item Subtotal */}
                                        <div className="flex sm:flex-col items-end justify-between sm:justify-start gap-4 w-full sm:w-auto shrink-0 pt-4 sm:pt-0 border-t sm:border-t-0 border-slate-100">
                                            {hasPricingForProduct(prod) ? (
                                                <div className="text-left sm:text-right flex flex-col">
                                                    <span className="text-[10px] font-black text-slate-500 uppercase tracking-wider">Item Total</span>
                                                    <span className="text-lg font-black text-[#2C3E2B] mt-0.5">
                                                        ₹{(itemPrice * (Number(item.quantity) || 0)).toFixed(2)}
                                                    </span>
                                                    <span className="text-[11px] font-bold text-slate-500 mt-0.5">
                                                        ₹{itemPrice.toFixed(2)} each
                                                    </span>
                                                </div>
                                            ) : (
                                                <div className="text-left sm:text-right flex flex-col">
                                                    <span className="text-[10px] font-black text-slate-500 uppercase tracking-wider">Price</span>
                                                    <span className="text-xs font-bold text-slate-500 mt-0.5 italic">Quote required</span>
                                                </div>
                                            )}

                                            <div className="flex items-center gap-3">
                                                {/* Qty Selector */}
                                                <div className="flex items-center bg-slate-50 border border-slate-200/60 rounded-xl px-2 py-1 gap-2">
                                                    <button
                                                        onClick={() => updateQuantity(prod, Math.max(prod.minimum_order || 1, (Number(item.quantity) || 0) - prod.minimum_order))}
                                                        className="w-6 h-6 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 flex items-center justify-center cursor-pointer transition"
                                                        aria-label="Decrease quantity"
                                                    >
                                                        <Minus className="w-3 h-3 text-slate-600" />
                                                    </button>
                                                    <input
                                                        type="number"
                                                        value={item.quantity === '' ? '' : Math.max(Number(item.quantity) || 0, prod.minimum_order || 1)}
                                                        min={prod.minimum_order || 1}
                                                        onChange={(e) => handleQtyChange(prod, e)}
                                                        onBlur={() => handleQtyBlur(prod, item)}
                                                        className="w-12 text-center text-xs font-black text-slate-800 bg-transparent border-none outline-none focus:ring-0 p-0"
                                                    />
                                                    <button
                                                        onClick={() => updateQuantity(prod, (Number(item.quantity) || 0) + prod.minimum_order)}
                                                        className="w-6 h-6 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 flex items-center justify-center cursor-pointer transition"
                                                        aria-label="Increase quantity"
                                                    >
                                                        <Plus className="w-3 h-3 text-slate-600" />
                                                    </button>
                                                </div>

                                                <button
                                                    onClick={() => removeItem(prod)}
                                                    className="w-9 h-9 rounded-xl bg-red-50 text-red-500 hover:bg-red-100 flex items-center justify-center border-none cursor-pointer transition"
                                                    title="Remove item"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}

                            {/* Easy Order Save Template Block */}
                            {auth.currentUser && (
                                <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-xs text-left">
                                    {!showEasyOrderInput ? (
                                        <button
                                            onClick={() => {
                                                setShowEasyOrderInput(true);
                                                fetchExistingEasyOrders();
                                            }}
                                            className="text-xs text-[#2C3E2B] hover:text-[#9E2A2B] font-extrabold bg-transparent border-none cursor-pointer flex items-center gap-2 transition"
                                        >
                                            <span>💾 Save this cart list as an Easy Order template</span>
                                        </button>
                                    ) : (
                                        <div className="flex flex-col gap-3 max-w-lg">
                                            <span className="text-[10px] font-black text-slate-500 uppercase tracking-wider block">Save to Easy Order List</span>

                                            {existingEasyOrders.length > 0 && (
                                                <div className="flex flex-col gap-1">
                                                    <label className="text-xs font-bold text-slate-600">Select List</label>
                                                    <select
                                                        value={selectedEasyOrderId}
                                                        onChange={(e) => setSelectedEasyOrderId(e.target.value)}
                                                        className="px-3 py-2 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#2C3E2B] bg-white text-slate-800 font-semibold"
                                                    >
                                                        <option value="new">+ Create New Easy Order List</option>
                                                        {existingEasyOrders.map((list) => (
                                                            <option key={list.id} value={list.id}>
                                                                {list.name} ({list.items?.length || 0} items)
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            )}

                                            {selectedEasyOrderId === 'new' && (
                                                <div className="flex flex-col gap-1">
                                                    <label className="text-xs font-bold text-slate-600">Template Name</label>
                                                    <input
                                                        type="text"
                                                        placeholder="e.g. Weekly Beverage Order"
                                                        value={easyOrderListName}
                                                        onChange={(e) => setEasyOrderListName(e.target.value)}
                                                        className="px-3 py-1.5 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#2C3E2B] bg-white text-slate-800"
                                                    />
                                                </div>
                                            )}

                                            <div className="flex gap-2 pt-1">
                                                <button
                                                    onClick={handleSaveEasyOrder}
                                                    disabled={isSavingEasyOrder}
                                                    className="px-4 py-1.5 bg-[#2C3E2B] text-white text-xs font-black rounded-lg border-none cursor-pointer disabled:opacity-50 hover:bg-[#1e2d1e] transition"
                                                >
                                                    {isSavingEasyOrder ? "Saving..." : selectedEasyOrderId === 'new' ? "Create & Save" : "Add to List"}
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setShowEasyOrderInput(false);
                                                        setEasyOrderListName('');
                                                        setSelectedEasyOrderId('new');
                                                    }}
                                                    className="px-3 py-1.5 bg-slate-200 text-slate-600 text-xs font-black rounded-lg border-none cursor-pointer hover:bg-slate-300 transition"
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Order Summary Right Column */}
                        <div className="lg:col-span-1">
                            <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-xs sticky top-28 text-left space-y-6">
                                <h3 className="text-lg font-black text-[#2C3E2B] m-0 border-b border-slate-100 pb-4">Order Summary</h3>

                                <div className="space-y-3">
                                    {/* Subtotal — hidden when there are unpriced items */}
                                    {!hasUnpricedItems && (
                                        <div className="space-y-3">
                                            <div className="flex justify-between text-sm pb-2 border-b border-slate-100">
                                                <span className="text-slate-500 font-semibold">Items</span>
                                                <span className="font-extrabold text-[#2C3E2B]">
                                                    {cart.length} {cart.length === 1 ? 'item' : 'items'} · {cart.reduce((s, i) => s + (Number(i.quantity) || 0), 0)} units
                                                </span>
                                            </div>
                                            <div className="flex justify-between text-sm">
                                                <span className="text-slate-500 font-semibold">Subtotal</span>
                                                <span className="font-extrabold text-[#2C3E2B]">₹{subtotal.toFixed(2)}</span>
                                            </div>
                                            <div className="flex justify-between text-sm">
                                                <span className="text-slate-500 font-semibold">Delivery</span>
                                                <span className="font-extrabold text-[#2C3E2B]">₹0.00</span>
                                            </div>
                                            <div className="border-t border-slate-100 pt-4 flex justify-between items-baseline">
                                                <span className="text-base font-extrabold text-[#2C3E2B]">Total</span>
                                                <span className="text-2xl font-black text-[#2C3E2B]">₹{subtotal.toFixed(2)}</span>
                                            </div>
                                        </div>
                                    )}
                                    {hasUnpricedItems && subtotal > 0 && (
                                        <div className="space-y-3">
                                            <div className="flex justify-between text-sm">
                                                <span className="text-slate-500 font-semibold">Priced Items Subtotal</span>
                                                <span className="font-extrabold text-[#2C3E2B]">₹{subtotal.toFixed(2)}</span>
                                            </div>
                                            <p className="text-[11px] text-slate-500 font-semibold">Some items require a quote — final total will be confirmed.</p>
                                        </div>
                                    )}
                                </div>

                                <div className="space-y-3">
                                    {schemeId && !hasUnpricedItems && (
                                        <Link to={hasInsufficientStockItems ? '#' : '/checkout'} className="no-underline block">
                                            <button
                                                disabled={hasInsufficientStockItems}
                                                className={`w-full py-3.5 bg-[#2C3E2B] hover:bg-[#1e2d1e] text-white font-black text-sm rounded-2xl border-none cursor-pointer transition shadow-md hover:shadow-lg hover:-translate-y-0.5 ${hasInsufficientStockItems ? 'opacity-50 cursor-not-allowed bg-slate-400' : ''
                                                    }`}
                                            >
                                                Proceed to Checkout
                                            </button>
                                        </Link>
                                    )}
                                    {/* Request a Quote — always shown when unpriced items exist */}
                                    {(hasUnpricedItems || !schemeId) && (
                                        <button
                                            onClick={() => {
                                                if (!auth.currentUser) {
                                                    toast.error("Please sign in to request a quote.");
                                                    window.dispatchEvent(new Event('open-auth'));
                                                    return;
                                                }
                                                openQuote({
                                                    cart: true,
                                                    products: cart.filter(item => !hasPricingForProduct(item.product)).map(item => ({
                                                        productName: item.product.productName,
                                                        category: item.product.categoryName || item.product.brandName || "N/A",
                                                        cart_quantity: item.quantity,
                                                        productId: item.product.id || item.product.objectID || ""
                                                    }))
                                                });
                                            }}
                                            className="w-full py-3.5 bg-[#F3E9DC] border border-[#547454] hover:border-[#395639] text-[#395639] hover:text-[#2C3E2B] text-sm font-bold rounded-2xl cursor-pointer transition shadow-sm"
                                        >
                                            Request a Quote
                                        </button>
                                    )}

                                    <Link to="/products" className="no-underline block text-center mt-2">
                                        <span className="text-xs font-bold text-slate-500 hover:text-[#2C3E2B] transition flex items-center justify-center gap-1.5">
                                            <ArrowLeft className="w-3.5 h-3.5" /> Continue Shopping
                                        </span>
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="bg-white rounded-3xl border border-slate-100 p-12 text-center max-w-lg mx-auto shadow-xs">
                        <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6 border border-slate-100">
                            <ShoppingBag className="w-8 h-8 text-slate-300" />
                        </div>
                        <h3 className="text-xl font-extrabold text-[#2C3E2B] mb-2">Your Cart is Empty</h3>
                        <p className="text-sm text-slate-500 mb-8 leading-relaxed">
                            Looks like you haven't added any products to your cart yet. Explore our wholesale catalog to get started.
                        </p>
                        <Link to="/products" className="no-underline">
                            <button className="px-8 py-3.5 bg-[#2C3E2B] hover:bg-[#1e2d1e] text-white font-black text-sm rounded-2xl border-none cursor-pointer transition shadow-md hover:-translate-y-0.5">
                                Browse Wholesale Products
                            </button>
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
}
